import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ADMIN_PANEL_ROLES } from '../utils/permissions';
import { API_BASE_URL } from '../config';

/**
 * A route wrapper that checks for authentication and required roles
 * before allowing access to protected routes.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The protected component to render
 * @param {string} [props.requiredRole] - Optional single role required to access the route
 * @param {string[]} [props.allowedRoles] - Optional array of roles allowed to access the route
 * @param {string} [props.redirectPath='/'] - Path to redirect to if authentication fails
 */

/**
 * Synchronous read of auth state from localStorage - no network, no await.
 * Used to compute React's initial state so a route with an already-valid
 * session renders immediately instead of flashing an "Authenticating..."
 * spinner on every navigation (each route mounts its own ProtectedRoute).
 * `needsRefresh` is true only for the rare expired-token case, which still
 * has to go through the async refresh path in the effect below.
 */
function computeAuthState(requiredRole, allowedRoles) {
    const accessToken = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
    const userRole = localStorage.getItem('user_role');

    if (!accessToken) {
        return { isAuthenticated: false, isAuthorized: false, needsRefresh: false };
    }

    let isAuthenticated = true;
    let needsRefresh = false;
    try {
        const tokenData = JSON.parse(atob(accessToken.split('.')[1]));
        if (tokenData.exp * 1000 < Date.now()) {
            needsRefresh = true;
            isAuthenticated = false; // provisional; the refresh in the effect will confirm
        }
    } catch (e) {
        // If we can't decode the token, assume it's valid
        isAuthenticated = true;
    }

    let hasValidRole = true;
    if (allowedRoles && Array.isArray(allowedRoles)) {
        hasValidRole = allowedRoles.includes(userRole);
    } else if (requiredRole) {
        hasValidRole = requiredRole === 'admin' ? ADMIN_PANEL_ROLES.includes(userRole) : userRole === requiredRole;
    }

    return { isAuthenticated, isAuthorized: hasValidRole, needsRefresh };
}

const ProtectedRoute = ({
    children,
    requiredRole = null,
    allowedRoles = null,
    redirectPath = '/'
}) => {
    const initial = computeAuthState(requiredRole, allowedRoles);
    const [isAuthenticated, setIsAuthenticated] = useState(initial.isAuthenticated);
    const [isAuthorized, setIsAuthorized] = useState(initial.isAuthorized);
    const [isLoading, setIsLoading] = useState(initial.needsRefresh);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                // Check if we have a valid access token
                const accessToken = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
                const userRole = localStorage.getItem('user_role');

                if (!accessToken) {
                    setIsAuthenticated(false);
                    setIsAuthorized(false);
                    return;
                }

                // Check token expiration
                try {
                    // Simple expiration check - this assumes your token has an 'exp' claim
                    // For a more secure implementation, you might want to validate the token with your backend
                    const tokenData = JSON.parse(atob(accessToken.split('.')[1]));
                    if (tokenData.exp * 1000 < Date.now()) {
                        // Token expired, attempt to refresh
                        await refreshToken();
                    } else {
                        setIsAuthenticated(true);
                    }
                } catch (e) {
                    // If we can't decode the token, assume it's valid
                    setIsAuthenticated(true);
                }

                // Check role authorization
                let hasValidRole = true;

                // If allowedRoles array is provided, check against it
                if (allowedRoles && Array.isArray(allowedRoles)) {
                    hasValidRole = allowedRoles.includes(userRole);
                }
                // If requiredRole string is provided, check for exact match
                else if (requiredRole) {
                    // Support 'admin' as shorthand for all admin panel roles
                    if (requiredRole === 'admin') {
                        hasValidRole = ADMIN_PANEL_ROLES.includes(userRole);
                    } else {
                        hasValidRole = userRole === requiredRole;
                    }
                }

                setIsAuthorized(hasValidRole);
            } catch (error) {
                console.error('Authentication check failed:', error);
                setIsAuthenticated(false);
                setIsAuthorized(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, [requiredRole, allowedRoles]);

    const refreshToken = async () => {
        try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
                setIsAuthenticated(false);
                return;
            }

            const response = await fetch(`${API_BASE_URL}/auth/token/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refresh_token: refreshToken })
            });

            if (!response.ok) {
                throw new Error('Token refresh failed');
            }

            const data = await response.json();
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('accessToken', data.access_token); // For backwards compatibility

            if (data.refresh_token) {
                localStorage.setItem('refresh_token', data.refresh_token);
            }

            setIsAuthenticated(true);
        } catch (error) {
            console.error('Token refresh failed:', error);
            setIsAuthenticated(false);

            // Clear all auth data on refresh failure
            localStorage.removeItem('access_token');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_role');
            localStorage.removeItem('school_id');
            localStorage.removeItem('school_name');
            localStorage.removeItem('user_email');
        }
    };

    if (isLoading) {
        // You could show a loading spinner here
        return <div className="loading-container">
            <div className="loader"></div>
            <p>Authenticating...</p>
        </div>;
    }

    if (!isAuthenticated) {
        return <Navigate to={redirectPath} replace />;
    }

    if (!isAuthorized) {
        // User is authenticated but not authorized for this route
        return <Navigate to="/unauthorized" replace />;
    }

    return children;
};

export default ProtectedRoute;